import { useEffect, useState } from "react";
import { platformTeamApi, type PlatformTeamUser } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useConfirm } from "../../components/ConfirmDialog";
import { useAppPreferences } from "../../lib/preferences";

export function PlatformTeamPanel() {
  const { user: actor } = useAuth();
  const { t } = useAppPreferences();
  const { confirm, dialog } = useConfirm();
  const [users, setUsers] = useState<PlatformTeamUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "support">("support");
  async function load() {
    setLoading(true); setError(null);
    try { setUsers((await platformTeamApi.list()).users); }
    catch (error) { setError(error instanceof Error ? error.message : t("Unable to load the team.")); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(); }, []);
  async function run(action: () => Promise<void>) {
    setBusy(true); setError(null); setNotice(null);
    try { await action(); }
    catch (error) { setError(error instanceof Error ? error.message : t("Unable to update team access.")); }
    finally { setBusy(false); }
  }
  async function changeAccess(user: PlatformTeamUser, next: { role: "admin" | "support"; is_active: boolean }) {
    if (!await confirm({ title: t("Change access for {name}?", { name: user.display_name }),
      description: next.is_active
        ? t("{email} will have {role} access. Existing sessions will be signed out.", { email: user.email, role: t(next.role === "admin" ? "Admin" : "Support") })
        : t("{email} will lose access. Existing sessions will be signed out.", { email: user.email }),
      confirmButtonLabel: t("Update access"), cancelButtonLabel: t("Cancel") })) return;
    await run(async () => {
      const updated = await platformTeamApi.update(user, next);
      setUsers(current => current.map(item => item.id === updated.id ? updated : item));
      setNotice(t("Access updated for {name}.", { name: updated.display_name }));
    });
  }
  return <section className="mx-auto grid max-w-4xl gap-5">
    {dialog}
    <div><h2 className="text-xl font-semibold">{t("Platform team")}</h2>
      <p className="mt-2 text-sm text-[var(--color-text-soft)]">{t("Admins manage accounts, commission, assistance and this team. Support can review queues, help with account access and add notes.")}</p></div>
    {error && <div role="alert" className="ops-banner" data-tone="danger">{error} <button className="ops-button" type="button" onClick={() => void load()}>{t("Refresh team")}</button></div>}
    {notice && <p role="status" className="ops-banner" data-tone="success">{notice}</p>}
    <form className="grid gap-4 rounded-xl border border-[var(--color-border-sub)] p-5 sm:grid-cols-2" onSubmit={event => {
      event.preventDefault();
      void run(async () => {
        const result = await platformTeamApi.invite({ display_name: name.trim(), email: email.trim(), role });
        setUsers(current => [...current, result.user]); setName(""); setEmail("");
        setNotice(result.email_sent ? t("Invitation sent to {email}. The link lasts seven days.", { email: result.user.email }) : t("Account created, but the email was not delivered. Retry the invitation below."));
      });
    }}>
      <h3 className="font-semibold sm:col-span-2">{t("Invite a teammate")}</h3>
      <label className="grid gap-2 text-sm">{t("Full name")}<input className="ops-input" required minLength={2} maxLength={255} value={name} onChange={e => setName(e.target.value)} /></label>
      <label className="grid gap-2 text-sm">{t("Email")}<input className="ops-input" required type="email" value={email} onChange={e => setEmail(e.target.value)} /></label>
      <label className="grid gap-2 text-sm">{t("Access")}<select className="ops-input" value={role} onChange={e => setRole(e.target.value as "admin" | "support")}><option value="support">{t("Support")}</option><option value="admin">{t("Admin")}</option></select></label>
      <button className="ops-button self-end" data-variant="primary" type="submit" disabled={busy || name.trim().length < 2}>{busy ? t("Saving…") : t("Send invitation")}</button>
      <p className="text-xs text-[var(--color-text-soft)] sm:col-span-2">{t("They choose their own password. No access is available until setup is complete.")}</p>
    </form>
    {loading ? <p role="status">{t("Loading team…")}</p> : <div className="grid gap-3">{users.map(user => <article key={user.id} className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-[var(--color-border-sub)] p-4">
      <div><p className="font-semibold">{user.display_name}{user.id === actor?.id ? ` ${t("(you)")}` : ""}</p><p className="text-sm text-[var(--color-text-soft)]">{user.email}</p>
        <p className="mt-1 text-xs">{!user.is_active ? t("Deactivated") : user.setup_pending ? t("Awaiting password setup") : t("Active")}</p></div>
      <div className="flex flex-wrap items-center gap-2">
        <select aria-label={t("Role for {email}", { email: user.email })} className="ops-input" disabled={busy || user.id === actor?.id} value={user.role} onChange={e => void changeAccess(user, { role: e.target.value as "admin" | "support", is_active: user.is_active })}><option value="support">{t("Support")}</option><option value="admin">{t("Admin")}</option></select>
        {user.setup_pending && user.is_active && <button className="ops-button" type="button" disabled={busy} onClick={() => void run(async () => {
          const result = await platformTeamApi.resend(user.id);
          setNotice(result.email_sent ? t("Invitation sent to {email}.", { email: user.email }) : t("Email delivery failed. You can retry the invitation."));
        })}>{t("Resend invitation")}</button>}
        {user.id !== actor?.id && <button className="ops-button" type="button" disabled={busy} onClick={() => void changeAccess(user, { role: user.role, is_active: !user.is_active })}>{user.is_active ? t("Deactivate") : t("Reactivate")}</button>}
      </div>
    </article>)}</div>}
  </section>;
}
